import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, ArrowRight, Download, ShieldCheck, TrendingUp, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import StatusPill from "@/components/marketplace/StatusPill";

export default function EarningsPage() {
  const [balances, setBalances] = useState({ pending: 0, available: 0, reserved: 0 });
  const [ledger, setLedger] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  useDocumentTitle("Earnings & payouts");

  const load = async () => {
    setLoading(true);
    try {
      const [b, l, p, a] = await Promise.all([
        api.get("/payouts/balances"),
        api.get("/payouts/ledger"),
        api.get("/payouts/list"),
        api.get("/payouts/account"),
      ]);
      setBalances(b.data || { pending: 0, available: 0, reserved: 0 });
      setLedger(l.data || []);
      setPayouts(p.data || []);
      setAccount(a.data);
    } catch (err) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startOnboarding = async () => {
    try {
      await api.post("/payouts/account/start");
      toast.success("Onboarding started");
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const completeOnboarding = async () => {
    try {
      await api.post("/payouts/account/complete");
      toast.success("Test payout account activated");
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const sweep = async () => {
    try {
      const { data } = await api.post("/payouts/sweep");
      toast.success(`Moved ${data.moved} entries to available`);
      load();
    } catch (err) { toast.error("Sweep failed"); }
  };
  const release = async () => {
    try {
      const { data } = await api.post("/payouts/release");
      if (data.ok) toast.success(`Payout of $${data.payout.amount.toFixed(2)} scheduled`);
      else toast.info(data.reason || "Nothing to release");
      load();
    } catch (err) { toast.error("Release failed"); }
  };
  const downloadCsv = () => {
    window.location.href = "/api/payouts/statement.csv";
  };

  return (
    <DashboardShell>
      <div className="max-w-6xl">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Earnings</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your money on Gulf Coast Work</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Provider payouts release {" "}
              <b>3 days</b> after a job is completed. All test-mode until live Stripe Connect keys are configured.
            </p>
          </div>
          <div className="flex gap-2">
            <button className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium inline-flex items-center gap-2" onClick={downloadCsv} data-testid="earnings-csv"><Download className="h-4 w-4" /> Statement CSV</button>
            <button className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium" onClick={sweep} data-testid="earnings-sweep">Sweep pending</button>
            <button className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" onClick={release} data-testid="earnings-release">Release payout</button>
          </div>
        </div>

        {/* Balances */}
        <div className="grid gap-4 sm:grid-cols-3 mb-6" data-testid="balance-cards">
          <BalanceCard label="Pending" value={balances.pending} accent="amber" icon={Clock} note="Locked until 3 days after completion" />
          <BalanceCard label="Available" value={balances.available} accent="emerald" icon={CheckCircle2} note="Ready to be paid out" />
          <BalanceCard label="Reserved" value={balances.reserved} accent="slate" icon={AlertTriangle} note="Held for open disputes" />
        </div>

        {/* Payout account */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="payout-account-card">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-xl bg-[#165a71]/10 flex items-center justify-center text-[#165a71]"><Wallet className="h-5 w-5" /></div>
            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-display font-semibold text-[#071b22]">Payout account</div>
                {account && <StatusPill status={account.status} />}
              </div>
              {account ? (
                <div className="text-sm text-[#252A34]/75 mt-1">
                  {account.display_name || "Simulated bank account"} · <span className="font-mono">{account.external_id}</span>
                  <div className="text-xs text-[#252A34]/60 mt-1 inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Test payout — live payouts activate when Stripe Connect keys are configured.</div>
                </div>
              ) : (
                <div className="text-sm text-[#252A34]/70 mt-1">You don't have a payout account yet. Start onboarding to receive earnings.</div>
              )}
              <div className="mt-3 flex gap-2">
                {!account && <button onClick={startOnboarding} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="onboard-start">Start onboarding</button>}
                {account && account.status !== "test_ready" && account.provider_kind === "simulated" && <button onClick={completeOnboarding} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="onboard-complete">Complete onboarding</button>}
              </div>
            </div>
          </div>
        </div>

        {/* Payouts + ledger */}
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-lg font-semibold text-[#071b22] mb-2">Recent payouts</h2>
            {loading ? <div className="text-sm text-[#252A34]/60">Loading…</div> : (
              payouts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-[#252A34]/70">No payouts yet.</div>
              ) : (
                <div className="space-y-2" data-testid="payouts-list">
                  {payouts.map((p) => (
                    <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid={`payout-${p.id}`}>
                      <div>
                        <div className="font-display font-semibold text-[#071b22]">${(p.amount || 0).toFixed(2)}</div>
                        <div className="text-xs text-[#252A34]/60 mt-1">{p.executed_at ? new Date(p.executed_at).toLocaleDateString() : (p.created_at ? new Date(p.created_at).toLocaleDateString() : "")}</div>
                      </div>
                      <StatusPill status={p.status} />
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-[#071b22] mb-2">Ledger</h2>
            {ledger.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-[#252A34]/70">No entries yet — earnings appear here once bookings complete.</div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white divide-y max-h-[520px] overflow-y-auto" data-testid="ledger-list">
                {ledger.map((e) => {
                  const debit = e.entry_type.endsWith("_debit");
                  return (
                    <div key={e.id} className="p-3 flex items-center gap-3">
                      <div className={`h-7 w-7 rounded-lg text-xs font-semibold flex items-center justify-center ${debit ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                        {debit ? "−" : "+"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#252A34] truncate">{e.description || e.entry_type}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#252A34]/60">{e.entry_type.replace(/_/g, " ")} · {e.balance_bucket}</div>
                      </div>
                      <div className={`text-sm font-medium ${debit ? "text-red-600" : "text-emerald-700"}`}>{debit ? "-" : "+"}${(e.amount || 0).toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function BalanceCard({ label, value, accent, icon: Icon, note }) {
  const bg = {
    emerald: "border-emerald-200 bg-emerald-50",
    amber: "border-amber-200 bg-amber-50",
    slate: "border-slate-200 bg-slate-50",
  }[accent];
  const text = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    slate: "text-[#252A34]",
  }[accent];
  return (
    <div className={`rounded-2xl border p-5 ${bg}`} data-testid={`balance-${label.toLowerCase()}`}>
      <div className={`inline-flex h-9 w-9 rounded-xl items-center justify-center bg-white ${text}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 font-display text-3xl font-semibold text-[#071b22]">${(Number(value) || 0).toFixed(2)}</div>
      <div className={`text-xs uppercase tracking-wider mt-1 font-medium ${text}`}>{label}</div>
      <div className="text-[11px] text-[#252A34]/60 mt-2">{note}</div>
    </div>
  );
}
